import { type EnvironmentId, type ThreadId, type TurnId } from "@cmmd-forge/contracts";
import { type ComponentProps, type ReactElement } from "react";
import { type ServerThreadStatus } from "./serverThreadStatus";
import { type TaskContextModel } from "../command-center/TaskContextSurface";
import { TaskContextSurface } from "../command-center/TaskContextSurface";
import { PlanProposalCard } from "../master/PlanProposalCard";
import { WorkerComposerBanner } from "../master/WorkerComposerBanner";
import { WorkBar } from "../workbar/WorkBar";
import { WorkBarAgentsRow } from "../workbar/WorkBarAgentsRow";

type PlanProposalCardProps = ComponentProps<typeof PlanProposalCard>;
type WorkBarAgentsRowProps = ComponentProps<typeof WorkBarAgentsRow>;

export function ChatComposerPrepRows(props: {
  readonly activeThreadKind: "master" | "worker";
  readonly parentThreadId: ThreadId | null;
  readonly environmentId: EnvironmentId;
  readonly pendingAutoCreatePrompt:
    | {
        readonly items: PlanProposalCardProps["items"];
        readonly anchorKind: PlanProposalCardProps["anchorKind"];
        readonly anchorCmmdId: PlanProposalCardProps["anchorCmmdId"];
        readonly state?: "pending_approval" | "materializing" | undefined;
      }
    | null
    | undefined;
  readonly onApprovePlan: PlanProposalCardProps["onApprove"];
  readonly onEditPlan: PlanProposalCardProps["onEdit"];
  readonly onDiscardPlan: PlanProposalCardProps["onDiscard"];
  readonly showAgentsRow: boolean;
  readonly showWorkBar: boolean;
  readonly activeThreadTeam: WorkBarAgentsRowProps["team"];
  readonly onOpenWorkerThread: (targetThreadId: ThreadId) => void;
  readonly activeTaskContextModel: TaskContextModel | null;
  readonly onViewActiveTaskContext: () => void;
  readonly serverThreadStatus: ServerThreadStatus;
  readonly serverThreadSubscriptionError: string | null | undefined;
  readonly activePlan: { readonly turnId?: TurnId } | null;
}): ReactElement {
  return (
    <>
      <WorkerComposerBanner
        kind={props.activeThreadKind}
        parentThreadId={props.parentThreadId}
        environmentId={props.environmentId}
      />

      {props.pendingAutoCreatePrompt && props.activeThreadKind === "master" ? (
        <div className="px-3 pb-2 sm:px-5">
          <PlanProposalCard
            items={props.pendingAutoCreatePrompt.items}
            anchorKind={props.pendingAutoCreatePrompt.anchorKind}
            anchorCmmdId={props.pendingAutoCreatePrompt.anchorCmmdId}
            state={props.pendingAutoCreatePrompt.state ?? "pending_approval"}
            onApprove={props.onApprovePlan}
            onEdit={props.onEditPlan}
            onDiscard={props.onDiscardPlan}
          />
        </div>
      ) : null}

      {props.showWorkBar && props.showAgentsRow ? (
        <div className="px-3 pt-1 sm:px-5 sm:pt-2">
          <div className="mx-auto w-full min-w-0 max-w-3xl">
            <WorkBar>
              <WorkBarAgentsRow
                team={props.activeThreadTeam}
                onOpenThread={props.onOpenWorkerThread}
              />
            </WorkBar>
          </div>
        </div>
      ) : null}

      {props.activeTaskContextModel ? (
        <div className="px-3 pt-1 sm:px-5 sm:pt-2">
          <div className="mx-auto w-full min-w-0 max-w-3xl">
            <TaskContextSurface
              model={props.activeTaskContextModel}
              variant="strip"
              onViewTask={props.onViewActiveTaskContext}
            />
          </div>
        </div>
      ) : null}

      {props.serverThreadStatus === "missing" ? (
        <div className="px-3 pb-2 sm:px-5">
          <div className="rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive-foreground dark:bg-destructive/50">
            {props.serverThreadSubscriptionError ??
              "Thread not found or you no longer have access."}
          </div>
        </div>
      ) : null}
    </>
  );
}
